import { useRef, useState, useCallback, useEffect } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";

const SPEECH_KEY = process.env.REACT_APP_SPEECH_KEY;
const SPEECH_REGION = process.env.REACT_APP_SPEECH_REGION;
const SPEECH_ENDPOINT = process.env.REACT_APP_SPEECH_ENDPOINT;

const SILENCE_TIMEOUT_MS = 8000;

/**
 * Custom hook for streaming speech-to-text using Azure Cognitive Services Speech SDK.
 * Uses continuous (streaming) recognition — interim results are emitted as the user
 * speaks, and final results are appended to the existing text via onTranscript.
 *
 * @param {Object}   options
 * @param {Function} options.onTranscript  - Called with (finalText) each time a
 *                                           phrase is finalised by the recogniser.
 * @param {string}   [options.language]    - BCP-47 language code (default "en-US").
 */
export default function useSpeechToText({
  onTranscript,
  language = "en-US",
} = {}) {
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [error, setError] = useState(null);

  const recognizerRef = useRef(null);
  const silenceTimerRef = useRef(null);

  /** Tear down any active recogniser and release resources. */
  const cleanup = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    const recognizer = recognizerRef.current;
    if (!recognizer) return;

    try {
      recognizer.stopContinuousRecognitionAsync(
        () => {
          recognizer.close();
          recognizerRef.current = null;
        },
        (err) => {
          console.error("Error stopping recognition:", err);
          recognizer.close();
          recognizerRef.current = null;
        }
      );
    } catch {
      recognizer.close();
      recognizerRef.current = null;
    }
  }, []);

  /**
   * (Re)starts the 8-second silence watchdog.
   * Each time speech activity is detected this is called to push the deadline
   * forward. When it finally fires with no intervening speech, recognition stops.
   */
  const scheduleSilenceStop = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      cleanup();
      setIsListening(false);
      setInterimText("");
    }, SILENCE_TIMEOUT_MS);
  }, [cleanup]);

  /** Start streaming speech recognition. */
  const startListening = useCallback(() => {
    if (!SPEECH_KEY || !SPEECH_REGION) {
      setError(
        "Azure Speech credentials are not configured. " +
          "Set REACT_APP_SPEECH_KEY and REACT_APP_SPEECH_REGION."
      );
      return;
    }

    // Clean up any lingering session before starting a new one.
    cleanup();
    setError(null);
    setInterimText("");

    try {
      const speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(
        new URL(SPEECH_ENDPOINT),
        SPEECH_KEY
      );
      speechConfig.speechRecognitionLanguage = language;

      // Enable detailed output so interim results contain partial hypotheses.
      speechConfig.outputFormat = SpeechSDK.OutputFormat.Simple;

      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(
        speechConfig,
        audioConfig
      );

      // --- Streaming events ---

      // Fires while speech is being recognised (partial / interim results).
      recognizer.recognizing = (_sender, event) => {
        if (
          event.result.reason === SpeechSDK.ResultReason.RecognizingSpeech
        ) {
          setInterimText(event.result.text);
          scheduleSilenceStop(); // reset the silence watchdog on any speech activity
        }
      };

      // Fires when a phrase has been fully recognised (final result).
      recognizer.recognized = (_sender, event) => {
        if (
          event.result.reason === SpeechSDK.ResultReason.RecognizedSpeech &&
          event.result.text
        ) {
          onTranscript?.(event.result.text);
          setInterimText("");
          scheduleSilenceStop(); // reset after each completed phrase
        }
      };

      // Fires when the session is cancelled (error, timeout, etc.).
      recognizer.canceled = (_sender, event) => {
        if (event.reason === SpeechSDK.CancellationReason.Error) {
          setError(
            `Speech recognition error: ${event.errorDetails ?? "Unknown error"}`
          );
        }
        setIsListening(false);
        setInterimText("");
      };

      // Fires when the session ends (e.g. after stopContinuousRecognition).
      recognizer.sessionStopped = () => {
        setIsListening(false);
        setInterimText("");
      };

      recognizerRef.current = recognizer;

      recognizer.startContinuousRecognitionAsync(
        () => {
          setIsListening(true);
          scheduleSilenceStop(); // start the initial silence watchdog
        },
        (err) => {
          setError(`Failed to start recognition: ${err}`);
          setIsListening(false);
        }
      );
    } catch (err) {
      setError(`Speech SDK initialisation failed: ${err.message ?? err}`);
      setIsListening(false);
    }
  }, [language, cleanup, onTranscript, scheduleSilenceStop]);

  /** Stop streaming recognition gracefully. */
  const stopListening = useCallback(() => {
    cleanup();
    setIsListening(false);
    setInterimText("");
  }, [cleanup]);

  /** Toggle between start and stop. */
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Ensure we clean up when the component unmounts.
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  return {
    isListening,
    interimText,
    error,
    startListening,
    stopListening,
    toggleListening,
  };
}
